import { COUNTRIES } from "common/constants";
import { TableFormList } from "components/formList";
import {
  useCreateDebtorAddressesMutation,
  useDeleteDebtorAddressesMutation,
  useFetchDebtorAddressesQuery,
  useUpdateDebtorAddressesMutation,
} from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useMemo, useState } from "react";

export default function AddressesStep({
  debtorId = undefined,
  dataSource = undefined,
  handleDelete = undefined,
  handleSave = undefined,
}) {
  const isControlled = !!dataSource && !!handleDelete;

  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: debtorAddresses, isLoading } = useFetchDebtorAddressesQuery(
    {
      debtorId,
    },
    { skip: !debtorId },
  );
  const [updateDebtorAddress] = useUpdateDebtorAddressesMutation();
  const [deleteDebtorAddress] = useDeleteDebtorAddressesMutation();
  const [createDebtorAddress] = useCreateDebtorAddressesMutation();

  const [selectedCountry, setSelectedCountry] = useState("US");

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const addressTypeOptions = useMemo(() => {
    return constants?.addressTypes?.map(({ value, display }) => ({ value, label: display }));
  }, [constants?.addressTypes]);

  const stateOptions = useMemo(() => {
    if (selectedCountry === "CA") {
      return (
        constants?.canadianProvinces?.map((province) => ({
          label: `${province.display} (${province.value})`,
          value: province.value,
        })) ?? []
      );
    }
    return (
      constants?.states?.map((state) => ({
        label: `${state.display} (${state.value})`,
        value: state.value,
      })) ?? []
    );
  }, [selectedCountry, constants?.states, constants?.canadianProvinces]);

  const isStateSelect = selectedCountry === "US" || selectedCountry === "CA";

  const columns = useMemo(
    () => [
      {
        dataIndex: "isPrimary",
        editable: true,
        inputType: "checkbox",
        title: "Primary",
        valuePropName: "checked",
        render: (value) => (value ? "Yes" : "-"),
      },
      {
        dataIndex: "type",
        editable: true,
        inputType: "select",
        title: "Type",
        options: addressTypeOptions,
        render: (value) => addressTypeOptions?.find((opt) => opt.value === value)?.label ?? value,
      },
      {
        dataIndex: "country",
        editable: true,
        inputType: "select",
        title: "Country",
        options: COUNTRIES,
        rules: [
          {
            required: true,
            message: "Select country",
          },
        ],
        inputProps: {
          filterOption,
          showSearch: true,
        },
        onChange: (value) => setSelectedCountry(value),
      },
      {
        dataIndex: "address1",
        editable: true,
        inputType: "text",
        title: "Address 1",
        rules: [
          {
            required: true,
            message: "Enter Address 1",
          },
        ],
      },
      {
        dataIndex: "address2",
        editable: true,
        inputType: "text",
        title: "Address 2",
      },
      {
        dataIndex: "city",
        editable: true,
        inputType: "text",
        title: "City",
        rules: [
          {
            required: true,
            message: "Enter city",
          },
        ],
      },
      {
        dataIndex: "state",
        editable: true,
        inputType: isStateSelect ? "select" : "text",
        rules: [
          {
            required: true,
            message: isStateSelect ? "Select state/province" : "Enter state/province",
          },
        ],
        options: isStateSelect ? stateOptions : undefined,
        inputProps: isStateSelect
          ? {
              filterOption,
              showSearch: true,
            }
          : undefined,
        title: "State/Province",
        render: (value) => {
          const match =
            constants?.states?.find((s) => s.value === value) ??
            constants?.canadianProvinces?.find((p) => p.value === value);
          return match ? `${match.display} (${match.value})` : value;
        },
      },
      {
        dataIndex: "zipCode",
        editable: true,
        inputType: "text",
        rules:
          selectedCountry === "US"
            ? [
                {
                  required: true,
                  pattern: /^\d{5}(-\d{4})?$/,
                  message: "Must be a valid zip code",
                },
              ]
            : [
                {
                  required: true,
                  message: "Enter zip/postal code",
                },
              ],
        inputProps: {
          placeholder: selectedCountry === "CA" ? "A1A 1A1" : undefined,
        },
        title: "Zip/Postal Code",
      },
      {
        dataIndex: "notes",
        editable: true,
        inputType: "text",
        title: "Notes",
      },
    ],
    [addressTypeOptions, stateOptions, isStateSelect, selectedCountry, constants],
  );

  const data = isControlled ? dataSource : debtorAddresses;

  return (
    <TableFormList
      addText="Add Address"
      loading={isLoading}
      defaultValues={{ type: "mail", country: "US", isPrimary: data.length === 0 }}
      columns={columns}
      data={data}
      onEdit={(record) => setSelectedCountry(record.country ?? "US")}
      onDelete={(address) =>
        isControlled
          ? handleDelete(address)
          : deleteDebtorAddress({
              debtorId,
              address,
            })
      }
      onSave={(address) => {
        if (isControlled) return handleSave(address);

        const action = address.id ? updateDebtorAddress : createDebtorAddress;
        return action({
          debtorId,
          address,
        });
      }}
    />
  );
}
